import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Compass, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ═══════════════════════════════════════════════
// FROM THE FOUR PILLARS — standalone program.
// Copied from the VIP Coaching page. Free intro (The Body) + REV +
// framing; the deeper three pillars lock until the client has
// access ('four-pillars'). Edit content in the arrays below.
// ═══════════════════════════════════════════════

const PILLARS_IMG = 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/w7o9r493_magic%20layers.png';

const CAT_STYLES = {
  Physical: { chip: 'bg-emerald-100 text-emerald-800', ring: 'border-emerald-200', bg: 'bg-emerald-50/60', badge: 'bg-emerald-600' },
  Mental: { chip: 'bg-amber-100 text-amber-800', ring: 'border-amber-200', bg: 'bg-amber-50/60', badge: 'bg-amber-600' },
  Emotional: { chip: 'bg-sky-100 text-sky-800', ring: 'border-sky-200', bg: 'bg-sky-50/60', badge: 'bg-sky-600' },
  Spiritual: { chip: 'bg-violet-100 text-violet-800', ring: 'border-violet-200', bg: 'bg-violet-50/60', badge: 'bg-violet-600' },
};

const lessons = [
  {
    num: 1,
    title: 'The Body',
    category: 'Physical',
    categoryLabel: 'Physical',
    body: ['Build the physical foundation that supports health, vitality and healing.'],
    list: [
      { h: 'Nourish your body and support gut and metabolic health' },
      { h: 'Move, breathe, mobilize and restore healthy physical function' },
      { h: 'Support hydration, minerals, circulation and flow' },
      { h: 'Reduce the environmental burdens your body is navigating' },
      { h: "Support your body's natural pathways of digestion, elimination and recovery" },
    ],
    tags: ['Nutrition', 'Movement', 'Detox'],
  },
  {
    num: 2,
    title: 'The Mind',
    category: 'Mental',
    categoryLabel: 'Mental',
    body: ['The way we think shapes the way we experience ourselves, our health and our lives.'],
    list: [
      { h: 'Notice the thoughts and inner dialogue running in the background' },
      { h: 'Explore beliefs and patterns that may be keeping you stuck' },
      { h: 'Build thoughts and beliefs that support where you want to go' },
      { h: 'Understand expectation, perception and the mind-body connection' },
      { h: 'Create a vision that gives your healing and growth direction' },
    ],
    tags: ['Beliefs', 'Perception', 'Vision'],
  },
  {
    num: 3,
    title: 'The Emotional',
    category: 'Emotional',
    categoryLabel: 'Emotional',
    body: ['Create greater harmony between what you feel, how you respond and what your body carries.'],
    list: [
      { h: 'Build emotional well-being through greater awareness and understanding of what you feel' },
      { h: 'Strengthen emotional intelligence and self-awareness' },
      { h: 'Build greater capacity for regulation, expression and resilience' },
      { h: 'Unwind old emotional patterns and protective responses' },
      { h: 'Understand the language of emotions in the body' },
    ],
    tags: ['Awareness', 'Regulation', 'Resilience'],
  },
  {
    num: 4,
    title: 'The Energetic',
    category: 'Spiritual',
    categoryLabel: 'Energetic',
    body: ['Explore the less tangible layers of who you are and the energy you bring into your life.'],
    list: [
      { h: 'Step into yourself as an energetic being, knowing you are more energy than body' },
      { h: 'Cultivate coherence through breath, presence and intentional practice' },
      { h: 'Explore consciousness, connection and your relationship to something greater' },
      { h: 'Harness your energy as a source of personal empowerment' },
      { h: 'Deepen your connection to intuition and inner guidance' },
    ],
    tags: ['Energy', 'Coherence', 'Intuition'],
  },
];

const influences = [
  {
    title: 'Hierarchy of Needs',
    text: "If basic safety, finances, housing, or rest are unstable, higher-level wellness behaviors become unrealistic. This isn't failure—it's physiology and psychology.",
  },
  {
    title: 'Life Circumstances',
    text: 'Season of life matters. Caregiving, grief, transitions, menopause, injury, workload—these all shift capacity. The work adapts to the season, not the other way around.',
  },
  {
    title: 'Subconscious & Belief Systems',
    text: "Much of what drives behavior lives below awareness. Conditioning, early experiences, and inherited patterns quietly influence motivation, self-worth, and consistency. This is why effort alone doesn't create lasting change.",
  },
];

const FourPillarsPage = () => {
  const { hasAccess } = useAuth();
  const unlocked = hasAccess('four-pillars');
  const [openId, setOpenId] = useState(1);

  return (
    <div className="min-h-screen bg-cream py-12 px-4 pt-24" data-testid="four-pillars-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8" data-testid="back-to-programs">
          <ArrowLeft size={16} /> Back to Programs
        </Link>

        {/* Header / intro */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Compass size={16} className="text-[#8a6a5a]" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">The Four Pillars · Will You Give It A Year?</p>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-4">The Four Pillars for Creating a Path to Wellness</h1>
          <p className="text-stone-600 font-body leading-relaxed mb-4 max-w-2xl">
            Most people don't struggle with wellness because they don't care. They struggle because they don't know where to
            begin. These Pillars aren't rules or goals — they're reference points. Places to gently bring attention when life
            feels bumpy, heavy, or out of sync.
          </p>
          <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6 text-sm font-body text-stone-600 leading-relaxed">
            <p className="mb-1"><span className="text-forest font-medium">Everything is connected:</span></p>
            <p>The Body affects The Mind → The Mind shapes The Emotional → The Emotional moves The Energetics</p>
            <p>and the Energetics circles back to nourish The Body.</p>
          </div>
          <img src={PILLARS_IMG} alt="The Four Pillars" className="w-full max-w-md mx-auto rounded-2xl mb-6" data-testid="four-pillars-graphic" />

          {/* REV program (free) */}
          <div className="bg-forest text-cream rounded-2xl p-6 md:p-8 mb-6" data-testid="rev-program-intro">
            <p className="text-xs font-body uppercase tracking-[0.25em] text-cream/60 mb-2">The REV Program</p>
            <h2 className="font-heading text-2xl md:text-3xl mb-4">Regulate · Elevate · Vitalize</h2>
            <div className="space-y-3 font-body text-sm md:text-base leading-relaxed text-cream/90">
              <p>
                REV isn't about doing more. It's about coming home to yourself — and letting the most strained parts of
                you be gently supported until the whole system begins to regulate.
              </p>
              <p>
                This is the streamlined heart of my <span className="italic">9 Pillars</span> work, gathered into four
                living layers — <span className="text-cream font-medium">The Body</span>,{' '}
                <span className="text-cream font-medium">The Mind</span>,{' '}
                <span className="text-cream font-medium">The Emotional</span> and{' '}
                <span className="text-cream font-medium">The Energetic</span>. Nothing has been lost; it's simply been
                distilled so it's easier to feel where you are, and where your system is quietly asking for support.
              </p>
              <p>
                The four pillars aren't a checklist or a set of rules. They're reference points. Instead of asking
                "What should I fix?", REV invites a softer, truer question: <span className="italic">"Where am I asking
                for support right now?"</span>
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <div className="bg-cream/10 rounded-xl p-4">
                <p className="font-heading text-lg text-cream mb-1">Regulate</p>
                <p className="font-body text-xs text-cream/80 leading-relaxed">Settle the nervous system and create safety in the body, so real change becomes possible.</p>
              </div>
              <div className="bg-cream/10 rounded-xl p-4">
                <p className="font-heading text-lg text-cream mb-1">Elevate</p>
                <p className="font-body text-xs text-cream/80 leading-relaxed">Bring awareness and intention to your thoughts, emotions and choices — and lift how you meet yourself.</p>
              </div>
              <div className="bg-cream/10 rounded-xl p-4">
                <p className="font-heading text-lg text-cream mb-1">Vitalize</p>
                <p className="font-body text-xs text-cream/80 leading-relaxed">Reconnect to your energy, your aliveness and the fullest expression of you.</p>
              </div>
            </div>
            <p className="font-accent italic text-cream/90 mt-6">
              There's no rush here. Give it the time it asks for — a season, or a year — and let the work become less
              about discipline and more about attunement. This is how I changed my own life. It works.
            </p>
          </div>

          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-5">
            <h2 className="font-heading text-xl text-forest mb-2">The Purpose of the Pillars</h2>
            <p className="text-stone-600 font-body text-sm leading-relaxed mb-2">
              The Pillars are not meant to be worked on all at once. They are meant to be noticed. Rather than asking
              "What should I fix?" they invite a different question: <span className="italic">"Where is my system asking for support right now?"</span>
            </p>
            <p className="font-accent italic text-forest">"When the most strained areas are supported, the entire system begins to regulate."</p>
          </div>
        </motion.div>

        {/* Pillar windows */}
        <div className="space-y-3" data-testid="four-pillars-lessons">
          {lessons.map((l) => {
            const open = openId === l.num;
            const s = CAT_STYLES[l.category] || CAT_STYLES.Physical;
            const locked = l.num !== 1 && !unlocked;
            return (
              <div key={l.num} className={`bg-white rounded-2xl border ${open ? s.ring : 'border-stone-200'} overflow-hidden transition-colors`} data-testid={`four-pillars-lesson-${l.num}`}>
                <button
                  onClick={() => setOpenId(open ? null : l.num)}
                  className="w-full px-5 md:px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
                  data-testid={`four-pillars-lesson-toggle-${l.num}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full ${s.badge} text-white flex items-center justify-center font-heading flex-shrink-0`}>
                      {l.num}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-lg md:text-xl text-forest leading-tight">{l.title}</h3>
                        {locked && <Lock size={13} className="text-stone-400" />}
                      </div>
                      <span className={`inline-block mt-1 text-[10px] font-body uppercase tracking-wider px-2 py-0.5 rounded-full ${s.chip}`}>{l.categoryLabel}</span>
                    </div>
                  </div>
                  <ChevronDown size={20} className={`text-forest flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                      <div className={`px-5 md:px-6 pb-6 pt-1 ${s.bg}`}>
                        {locked ? (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white/70 border border-stone-200 rounded-xl p-4">
                            <p className="flex-1 font-body text-sm text-stone-600">
                              <Lock size={13} className="inline mr-1.5 -mt-0.5 text-stone-400" />
                              This pillar is part of the full Four Pillars journey.
                            </p>
                            <Link
                              to="/contact"
                              className="inline-flex items-center justify-center gap-1.5 bg-forest text-cream font-body text-sm px-4 py-2 rounded-full hover:bg-forest-dark transition-colors whitespace-nowrap"
                              data-testid={`four-pillars-unlock-${l.num}`}
                            >
                              Unlock the journey <ArrowRight size={14} />
                            </Link>
                          </div>
                        ) : (
                          <>
                            {l.body.map((p, i) => (
                              <p key={i} className="font-body text-stone-700 text-sm leading-relaxed mb-3">{p}</p>
                            ))}
                            {l.list && (
                              <ol className="space-y-2 mb-4">
                                {l.list.map((item, i) => (
                                  <li key={i} className="flex gap-3">
                                    <span className={`flex-shrink-0 w-6 h-6 rounded-full ${s.badge} text-white text-xs flex items-center justify-center font-body`}>{i + 1}</span>
                                    <p className="font-body text-sm text-stone-700 leading-snug">
                                      <span className="font-medium text-forest">{item.h}</span>
                                    </p>
                                  </li>
                                ))}
                              </ol>
                            )}
                            {l.tags && (
                              <div className="flex flex-wrap gap-2">
                                {l.tags.map((t) => (
                                  <span key={t} className={`text-xs font-body px-3 py-1 rounded-full ${s.chip}`}>{t}</span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Three influences */}
        <div className="mt-12" data-testid="four-pillars-influences">
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">The Three Influences That Shape Every Pillar</h2>
          <p className="font-body text-stone-600 text-sm leading-relaxed mb-5 max-w-2xl">
            Wellness does not exist in a vacuum. This is where compassion and a true holistic view can be very beneficial — and really very kind to self.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            {influences.map((inf) => (
              <div key={inf.title} className="bg-white border border-stone-200 rounded-2xl p-5">
                <h3 className="font-heading text-lg text-forest mb-2">{inf.title}</h3>
                <p className="font-body text-sm text-stone-600 leading-relaxed">{inf.text}</p>
              </div>
            ))}
          </div>
          <div className="bg-forest text-cream rounded-2xl p-6 mt-6">
            <p className="font-accent italic text-lg leading-relaxed">
              INTENTION is 9/10th of the law! When we set intentions... everything else arranges itself to fall into alignment
              with the primary intention for our highest good.
            </p>
          </div>
        </div>

        {/* One-year journey */}
        <div className="mt-12 bg-[#f5efe4] border border-stone-200 rounded-2xl p-6 md:p-8" data-testid="four-pillars-one-year">
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-3">How This Becomes a One-Year Journey</h2>
          <p className="font-body text-stone-700 text-sm leading-relaxed mb-3">
            Are you willing to give it a year? This is TRULY how I changed my life... this! This process here. It's why I share
            it. It works.
          </p>
          <p className="font-body text-stone-700 text-sm leading-relaxed mb-3">
            This framework isn't meant to be rushed — it is designed for your highest goals of longevity. Over a year, each
            pillar can be explored, revisited, and re-integrated as life evolves. The work becomes less about discipline and
            more about attunement. Less about fixing and more about listening. Less about pushing and more about stability and trust.
          </p>
          <p className="font-accent italic text-forest">
            "The smoothness of the wheel improves not when everything is perfect — but when the most strained areas are gently supported."
          </p>
        </div>
      </div>
    </div>
  );
};

export default FourPillarsPage;
